/* ==========================================================================
   Reefphyto - Chatbase floating chat widget, UK only
   ==========================================================================

   Loaded from snippets/chatbase.liquid, which is rendered once from
   layout/theme.liquid before the closing body tag.

   WHAT THIS DOES
   --------------
   The Chatbase embed is NOT in the page markup. Nothing is downloaded from
   chatbase.co until the code below decides the visitor is in the UK, then it
   builds the script element and appends it. A non-UK visitor never fetches the
   embed, so it cannot consume tokens. This is a real block, not CSS hiding.

   Country comes from Cloudflare's free public trace endpoint, which returns
   plain text including a line "loc=GB". No API key, no third-party geo service,
   no dependencies, just a fetch and a string parse.

   WHY THIS IS A .js FILE AND NOT INLINE LIQUID
   -------------------------------------------
   Inline scripts inside a .liquid snippet are parsed as raw text by Shopify's
   Liquid/HTML parser. A literal tag name in a comment or string can be read as
   real markup and break the file. An external asset has no such exposure and
   can be checked with `node --check` before it ever reaches the theme.

   Rules kept here deliberately:
     - never write a literal script tag in this file, opening or closing
     - build DOM with document.createElement, never with markup strings
     - set styles via element.style or a created style element, never raw HTML

   TESTING
   -------
   Add ?forcechat=1 to any URL to skip the geo check and load the widget.
   The detected country is always logged to the browser console.
   ========================================================================== */

(function () {
  'use strict';

  /* ==========================================================================
     SETTINGS
     ========================================================================== */

  // If the country lookup fails or times out, should the widget load anyway?
  // true  = fail open. A rare non-UK visitor may get through, but a UK customer
  //         on a bad connection never loses access to chat.
  // false = fail closed. No country, no widget.
  var FAIL_OPEN = true;

  // Two-letter ISO country code allowed to see the widget.
  var ALLOWED_COUNTRY = 'GB';

  // How long to wait for Cloudflare before giving up, in milliseconds.
  var TIMEOUT_MS = 2000;

  // Cloudflare's free public trace endpoint. Returns plain text, one key=value
  // per line, including "loc=GB" for a UK visitor.
  var TRACE_URL = 'https://www.cloudflare.com/cdn-cgi/trace';

  // Add ?forcechat=1 to any URL to bypass the geo check.
  var FORCE_PARAM = 'forcechat';

  /* ==========================================================================
     CHATBASE EMBED CONFIG - unchanged from the original embed
     ========================================================================== */

  var CHATBOT_ID = '1M24lUO7v9IOolJcOt7XF';
  var CHATBASE_DOMAIN = 'www.chatbase.co';
  var EMBED_SRC = 'https://www.chatbase.co/embed.min.js';

  // Inline in-page chat panel. The templates ship an EMPTY placeholder,
  // <div data-chatbase-inline>, and the iframe below is only built into it when
  // the visitor is allowed. Nothing from chatbase.co loads otherwise.
  var IFRAME_SRC = 'https://www.chatbase.co/chatbot-iframe/' + CHATBOT_ID;
  var INLINE_SELECTOR = '[data-chatbase-inline]';
  var INLINE_MIN_HEIGHT = '700px';

  /* ==========================================================================
     Loader
     ========================================================================== */

  // Guard against this file somehow running twice, which would give two
  // widgets and two script downloads.
  if (window.__rpChatbaseInit) return;
  window.__rpChatbaseInit = true;

  function loadChatbase(reason) {
    if (window.__rpChatbaseLoaded) return;
    window.__rpChatbaseLoaded = true;

    // This global MUST exist before embed.min.js executes. Same values as the
    // original inline config block it replaces.
    window.embeddedChatbotConfig = {
      chatbotId: CHATBOT_ID,
      domain: CHATBASE_DOMAIN
    };

    var s = document.createElement('script');
    s.src = EMBED_SRC;
    s.setAttribute('chatbotId', CHATBOT_ID);
    s.setAttribute('domain', CHATBASE_DOMAIN);

    // Note on defer: a script created this way and appended is asynchronous by
    // default, and the defer attribute is ignored for injected scripts. Setting
    // async = false keeps execution ordered, which is the closest equivalent to
    // the original defer behaviour.
    s.defer = true;
    s.async = false;

    (document.body || document.head || document.documentElement).appendChild(s);
    console.log('[chatbase] widget loaded (' + reason + ')');
  }

  function skipChatbase(reason) {
    console.log('[chatbase] widget NOT loaded (' + reason + ')');
    // The panel stays collapsed. The head CSS already hides it; this is the
    // fallback for browsers that do not support :has().
    hideInlinePanels();
    console.log('[chatbase] inline panel(s) collapsed, nothing loaded from chatbase.co');
  }

  // Find the container that should collapse when the panel is withheld: the
  // heading and the placeholder together, but never a whole page section.
  function panelContainer(host) {
    if (!host.closest) return null;
    var block = host.closest('.product-block-list__item');
    if (block) return block;
    var liquidSection = host.closest('section[data-section-type="liquid"]');
    if (liquidSection) return liquidSection.closest('.shopify-section') || liquidSection;
    return null;
  }

  // Reveal the panels. layout/theme.liquid hides them by default via
  // html:not([data-chatbase-ok]), so setting this attribute is what makes the
  // heading and box appear. Done before injection so there is no reflow gap.
  function revealInlinePanels() {
    document.documentElement.setAttribute('data-chatbase-ok', '1');
  }

  // Belt and braces for browsers without CSS :has() support, where the default
  // hide in the head cannot apply. Collapses the container directly.
  function hideInlinePanels() {
    var hosts = document.querySelectorAll(INLINE_SELECTOR);
    for (var i = 0; i < hosts.length; i++) {
      var container = panelContainer(hosts[i]);
      if (container) container.style.display = 'none';
      else hosts[i].style.display = 'none';
    }
  }

  // Fill every empty in-page placeholder with the chat iframe. Built entirely
  // with createElement/setAttribute, no markup strings anywhere.
  function injectInlinePanels() {
    var hosts = document.querySelectorAll(INLINE_SELECTOR);
    if (!hosts.length) return;

    var built = 0;
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];

      // Never fill the same placeholder twice.
      if (host.getAttribute('data-chatbase-ready') === '1') continue;
      host.setAttribute('data-chatbase-ready', '1');

      var frame = document.createElement('iframe');
      frame.setAttribute('src', IFRAME_SRC);
      frame.setAttribute('width', '100%');
      frame.setAttribute('frameborder', '0');
      frame.setAttribute('title', 'Reefphyto chat');
      frame.style.width = '100%';
      frame.style.height = '100%';
      frame.style.minHeight = INLINE_MIN_HEIGHT;
      frame.style.border = '0';

      host.appendChild(frame);
      built++;
    }

    if (built) console.log('[chatbase] inline panel(s) injected: ' + built);
  }

  // Single entry point for "this visitor is allowed". Drives BOTH the floating
  // bubble and the in-page panels from the one country lookup, so they can
  // never disagree.
  function enableChat(reason) {
    loadChatbase(reason);
    revealInlinePanels();
    injectInlinePanels();
  }

  /* ==========================================================================
     Testing override: ?forcechat=1
     ========================================================================== */

  try {
    if (new URLSearchParams(window.location.search).get(FORCE_PARAM) === '1') {
      enableChat('forced via ?' + FORCE_PARAM + '=1, geo check skipped');
      return;
    }
  } catch (e) {
    // URLSearchParams missing on a very old browser. Fall through to the geo
    // check rather than failing.
  }

  /* ==========================================================================
     Country check
     ========================================================================== */

  if (typeof fetch !== 'function') {
    console.log('[chatbase] detected country: unknown (fetch not supported)');
    if (FAIL_OPEN) enableChat('FAIL_OPEN, browser has no fetch');
    else skipChatbase('no fetch and FAIL_OPEN is false');
    return;
  }

  var controller = (typeof AbortController === 'function') ? new AbortController() : null;
  var timer = setTimeout(function () {
    if (controller) controller.abort();
  }, TIMEOUT_MS);

  fetch(TRACE_URL, controller ? { signal: controller.signal } : undefined)
    .then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.text();
    })
    .then(function (text) {
      clearTimeout(timer);

      // Trace body is one key=value per line. Anchor to line start so we cannot
      // accidentally match part of another key.
      var match = /(^|\n)loc=([A-Za-z]{2})/.exec(text);
      var country = match ? match[2].toUpperCase() : null;

      console.log('[chatbase] detected country: ' + (country || 'unknown (no loc= line)'));

      if (country === ALLOWED_COUNTRY) {
        enableChat('country is ' + country);
      } else if (country) {
        skipChatbase('country is ' + country + ', not ' + ALLOWED_COUNTRY);
      } else if (FAIL_OPEN) {
        enableChat('FAIL_OPEN, trace had no loc= line');
      } else {
        skipChatbase('no loc= line and FAIL_OPEN is false');
      }
    })
    .catch(function (err) {
      clearTimeout(timer);
      var why = (err && err.name === 'AbortError')
        ? 'timed out after ' + TIMEOUT_MS + 'ms'
        : String(err);
      console.log('[chatbase] country lookup failed: ' + why);

      if (FAIL_OPEN) enableChat('FAIL_OPEN after lookup failure');
      else skipChatbase('lookup failed and FAIL_OPEN is false');
    });
})();
